import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { NgxDatatableModule, ColumnMode } from '@swimlane/ngx-datatable';
import { Page, Pagination } from '../../../shared/models/page';
import { User, UserFilters } from '../../../shared/models/user';
import { UsersService } from '../../../core/services/users.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { UserDialogComponent } from '../dialogs/user-dialog/user-dialog.component';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Role } from '../../../shared/models/role';
import { RolesService } from '../../../core/services/roles.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, finalize, Subject, switchMap } from 'rxjs';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { AccountService } from '../../../core/services/account.service';
import { getImageUrlHelper } from '../../../shared/helpers/getImageUrl';
import { AlertType } from '../../../shared/models/alertType';
import { showAlertDialog } from '../../../shared/helpers/showAlertDialog';
import { LoadingService, LocalLoadingState } from '../../../core/services/loading.service';

@Component({
  selector: 'app-users',
  imports: [
    MatIconModule,
    CommonModule,
    NgxDatatableModule,
    MatTooltipModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    NgxPermissionsModule
  ],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss'
})
export class UsersComponent implements OnInit, OnDestroy {
  @ViewChild('statusSelect') statusSelect!: ElementRef<HTMLSelectElement>;
  @ViewChild('cargoSelect') cargoSelect!: ElementRef<HTMLSelectElement>;

  private loadPageSubject = new Subject<UserFilters>();
  private loader: LocalLoadingState;
  currentUser: User | null = null;
  users = new Array<User>();
  cargos: Role[] = [];
  columns = [
    { prop: 'name', name: 'Nome' },
    { prop: 'email', name: 'Email' },
    { prop: 'role', name: 'Cargo' },
    { prop: 'active', name: 'Status' },
    { name: 'Ações' }
  ];
  page: Page = { pageSize: 12, page: 0, count: 0 };
  ColumnMode = ColumnMode;

  sort: string = "normal";
  isActiveFilter: boolean | undefined = undefined;
  roleIdFilter: number | undefined = undefined;
  searchControl = new FormControl('');
  canCreateUser: boolean = false;
  canUpdateUser: boolean = false;

  get isLoading(): boolean {
    return this.loader.isLoading();
  }

  constructor(
    private userService: UsersService,
    private rolesService: RolesService,
    private dialog: MatDialog,
    private accountService: AccountService,
    private permissionsService: NgxPermissionsService,
    private loadingService: LoadingService,
  ) {
    this.loader = this.loadingService.createLocalLoader();
  }

  ngOnInit() {
    this.loadPageSubject.pipe(
      switchMap((userFilters: UserFilters) => {
        this.loader.show();
        return this.userService.getUsers(userFilters).pipe(
          finalize(() => this.loader.hide())
        );
      })
    ).subscribe((pagedData: Pagination<User>) => {
      this.page.count = pagedData.totalCount;
      this.users = pagedData.items;
    });

    this.loadPage();

    this.rolesService.getRoles({ page: 1, pageSize: 1000, sort: 'normal' }).subscribe((roles) => {
      this.cargos = roles.items;
      console.log(this.cargos)
    });

    this.searchControl.valueChanges
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        filter(() => !this.isLoading),
      )
      .subscribe((searchValue) => {
        this.page.page = 0;
        this.loadPage();
      });

    this.currentUser = this.userService.currentUser();
    this.permissionsService.hasPermission('Permissions.Users.Create').then(hasPermission => {
      this.canCreateUser = hasPermission;
    });
    this.permissionsService.hasPermission('Permissions.Users.Update').then(hasPermission => {
      this.canUpdateUser = hasPermission;
    });
  }

  changePageSize(event: Event) {
    const newSize = (event.target as HTMLSelectElement).value;
    this.page.pageSize = parseInt(newSize, 10);
    this.page.page = 0;
    this.loadPage();
  }

  onSearchKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.page.page = 0;
      this.loadPage();
    }
  }

  clearFiltros() {
    this.searchControl.setValue('');

    this.roleIdFilter = undefined;
    if (this.cargoSelect) {
      this.cargoSelect.nativeElement.value = "";
    }

    this.isActiveFilter = undefined;
    if (this.statusSelect) {
      this.statusSelect.nativeElement.value = "";
    }

    this.page.page = 0;
    this.loadPage();
  }

  onFilterByCargo(event: any) {
    const selectElement = event.target as HTMLSelectElement;
    if (selectElement.value.length == 0 || selectElement.value === undefined || selectElement.value === 'undefined') {
      this.roleIdFilter = undefined;
    } else {
      this.roleIdFilter = +selectElement.value;
    }
    this.page.page = 0;
    this.loadPage();
  }

  onFilterByStatus(event: any) {
    const selectElement = event.target as HTMLSelectElement;
    if (selectElement.value.length == 0 || selectElement.value === undefined || selectElement.value === 'undefined') {
      this.isActiveFilter = undefined;
    } else if (selectElement.value === 'true') {
      this.isActiveFilter = true;
    } else {
      this.isActiveFilter = false;
    }
    this.page.page = 0;
    this.loadPage();
  }

  onPage(event: { offset?: number; page?: number }) {
    if (event.page !== undefined) {
      this.page.page = event.page - 1;
    } else if (event.offset !== undefined) {
      this.page.page = event.offset;
    }
    this.loadPage();
  }

  onSort(event: { sorts: { prop: string, dir: string }[] }) {
    const sort = event.sorts[0];
    this.sort = sort.dir === 'asc' ? 'normal' : 'reverse';
    this.page.page = 0;
    this.loadPage();
  }

  loadPage() {
    const userFilters: UserFilters = {
      sort: this.sort,
      page: this.page.page + 1,
      pageSize: this.page.pageSize,
      name: (this.searchControl.value != null && this.searchControl.value.trim() !== '') ? this.searchControl.value : undefined,
      roleId: this.roleIdFilter,
      active: this.isActiveFilter,
    };
    this.loadPageSubject.next(userFilters);
  }

  openUserDialog(edit: boolean, user?: User) {
    const dialogData = {
      edit: edit,
      user: user,
    };

    const dialogRef = this.dialog.open(UserDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.loadPage();
      }
    });
  }

  toggleIsActive(user: User) {
    const alertDialogRef = user.active
      ? showAlertDialog(this.dialog, AlertType.Warning, 'Atenção!', 'Você tem certeza que deseja inativar este usuário?', 'Confirmar')
      : showAlertDialog(this.dialog, AlertType.Warning, 'Atenção!', 'Você tem certeza que deseja ativar este usuário?', 'Confirmar');
    alertDialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.userService.changeUserStatus(user.id!, !user.active).subscribe(res => {
          this.page.page = 0;
          this.loadPage();
          showAlertDialog(this.dialog, AlertType.Success, 'Sucesso!', 'Usuário alterado com sucesso!');
        });
      }
    });
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return format(date, 'MMM yyyy', { locale: ptBR });
  }

  getStartIndex(): number {
    if (this.page.count === 0) return 0;
    return this.page.page * this.page.pageSize + 1;
  }

  getEndIndex(): number {
    const end = (this.page.page + 1) * this.page.pageSize;
    return Math.min(end, this.page.count);
  }

  ngOnDestroy(): void {
    this.loader.destroy();
  }
}
